<template>
  <div class="dashboard-root">
    <!-- Superadmin Platform Operations View -->
    <SuperAdminDashboardView v-if="isSuperAdmin" />

    <!-- Game Owner / Game Maker Enterprise Workspace -->
    <div class="enterprise-workspace" v-else>
      <!-- Header & Action Toolbar -->
      <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 pb-3 border-bottom border-subtle">
        <div class="mb-3 mb-md-0">
          <div v-if="companyName" class="d-flex align-items-center mb-1">
            <span class="badge badge-outline-secondary text-white font-weight-medium mr-2" style="font-size: 11px;">
              {{ companyName }}
            </span>
            <span class="badge badge-outline-success font-weight-medium" style="font-size: 11px;">
              <i class="mdi mdi-check-circle mr-1"></i>{{ $t('workspace.companyActiveStatus') }}
            </span>
          </div>
          <h3 class="text-white font-weight-bold mb-1">
            {{ $t('workspace.title') }}
          </h3>
          <p class="text-muted small mb-0">
            {{ $t('workspace.subtitle') }}
          </p>
        </div>

        <div class="d-flex flex-wrap align-items-center gap-2">
          <!-- Timeframe selector -->
          <select
            v-model="timeframe"
            class="form-control form-control-sm bg-input border-card text-white mr-2"
            style="width: 140px; height: 36px;"
            @change="getGamificationData"
          >
            <option value="month">{{ $t('workspace.filterThisMonth') }}</option>
            <option value="all">{{ $t('workspace.filterAllTime') }}</option>
          </select>

          <!-- Refresh Telemetry Button -->
          <button
            class="btn btn-outline-secondary btn-sm mr-2 px-3"
            style="height: 36px;"
            @click="refreshAll"
            :disabled="loading"
          >
            <i class="mdi mdi-refresh mr-1" :class="{ 'mdi-spin': loading }"></i>
            {{ $t('workspace.refreshData') }}
          </button>

          <!-- Action Buttons -->
          <router-link
            to="/rules/rewards/add"
            class="btn btn-primary btn-sm px-3 font-weight-bold mr-2"
            style="height: 36px; display: inline-flex; align-items: center;"
          >
            <i class="mdi mdi-plus-circle-outline mr-1"></i>
            {{ $t('workspace.createQuestBtn') }}
          </router-link>

          <router-link
            to="/approval/reward"
            class="btn btn-outline-warning btn-sm px-3 font-weight-bold"
            style="height: 36px; display: inline-flex; align-items: center;"
          >
            <i class="mdi mdi-check-decagram-outline mr-1"></i>
            {{ $t('workspace.reviewSubmissionsBtn') }}
          </router-link>
        </div>
      </div>

      <!-- Executive Command Quick Access Bar -->
      <div class="row mb-4">
        <div v-if="isOwner" class="col-md-4 mb-3 mb-md-0">
          <router-link
            to="/owner/analytics"
            class="card text-decoration-none shadow-sm h-100"
            style="background: linear-gradient(135deg, #1e1b2e 0%, #111827 100%); border-left: 4px solid #f59e0b !important; border-radius: 10px;"
          >
            <div class="card-body p-3">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <span class="badge badge-warning text-dark font-weight-bold" style="font-size: 10px;">{{ $t('nav.roleOwner').toUpperCase() }}</span>
                <span class="text-warning small font-weight-bold">{{ $t('workspace.openSuite') }}</span>
              </div>
              <h5 class="text-white font-weight-bold mb-1">
                <i class="mdi mdi-crown text-warning mr-1" aria-hidden="true"></i> {{ $t('workspace.quickOwnerAnalytics') }}
              </h5>
              <p class="text-muted small mb-0">{{ $t('workspace.quickOwnerDesc') }}</p>
            </div>
          </router-link>
        </div>

        <div :class="isOwner ? 'col-md-4 mb-3 mb-md-0' : 'col-md-6 mb-3 mb-md-0'">
          <router-link
            to="/maker/studio"
            class="card text-decoration-none shadow-sm h-100"
            style="background: linear-gradient(135deg, #132427 0%, #0d1a24 100%); border-left: 4px solid #10b981 !important; border-radius: 10px;"
          >
            <div class="card-body p-3">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <span class="badge badge-success text-dark font-weight-bold" style="font-size: 10px;">{{ $t('nav.roleMaker').toUpperCase() }}</span>
                <span class="text-success small font-weight-bold">{{ $t('workspace.openStudio') }}</span>
              </div>
              <h5 class="text-white font-weight-bold mb-1">
                <i class="mdi mdi-hammer-wrench text-success mr-1" aria-hidden="true"></i> {{ $t('workspace.quickMakerStudio') }}
              </h5>
              <p class="text-muted small mb-0">{{ $t('workspace.quickMakerDesc') }}</p>
            </div>
          </router-link>
        </div>

        <div :class="isOwner ? 'col-md-4' : 'col-md-6'">
          <router-link
            to="/realms/marketplace"
            class="card text-decoration-none shadow-sm h-100"
            style="background: linear-gradient(135deg, #241d15 0%, #1c1815 100%); border-left: 4px solid #6366f1 !important; border-radius: 10px;"
          >
            <div class="card-body p-3">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <span class="badge badge-outline-primary font-weight-bold" style="font-size: 10px;">{{ $t('workspace.badgeWorkspaces') }}</span>
                <span class="text-primary small font-weight-bold">{{ $t('workspace.openMarket') }}</span>
              </div>
              <h5 class="text-white font-weight-bold mb-1">
                <i class="mdi mdi-cube-outline text-primary mr-1" aria-hidden="true"></i> {{ $t('workspace.quickRealmsMarket') }}
              </h5>
              <p class="text-muted small mb-0">{{ $t('workspace.quickRealmsDesc') }}</p>
            </div>
          </router-link>
        </div>
      </div>

      <!-- Core Gamification KPI Metric Cards -->
      <div class="row mb-4">
        <!-- Team Engagement Index -->
        <div class="col-sm-6 col-xl-3 grid-margin stretch-card">
          <div class="card shadow-sm h-100">
            <div class="card-body p-3">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <p class="text-muted mb-1 small font-weight-bold text-uppercase">{{ $t('workspace.engagementIndex') }}</p>
                  <h3 class="mb-0 text-white font-weight-bold">{{ gamification.engagementScore }}<span class="text-muted small">/100</span></h3>
                </div>
                <div class="p-2 rounded-lg" style="background: rgba(245, 158, 11, 0.12); border: 1px solid rgba(245, 158, 11, 0.25);">
                  <i class="mdi mdi-speedometer text-warning" style="font-size: 22px;" aria-hidden="true"></i>
                </div>
              </div>
              <div class="mt-2">
                <span class="badge badge-success">{{ formatEngagementLabel(gamification.engagementLabel) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Active Streaks -->
        <div class="col-sm-6 col-xl-3 grid-margin stretch-card">
          <div class="card shadow-sm h-100">
            <div class="card-body p-3">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <p class="text-muted mb-1 small font-weight-bold text-uppercase">{{ $t('workspace.activeStreaks') }}</p>
                  <h3 class="mb-0 text-warning font-weight-bold">{{ gamification.streakCount }} <span class="text-muted small">{{ $t('workspace.members') }}</span></h3>
                </div>
                <div class="p-2 rounded-lg" style="background: rgba(239, 68, 68, 0.12); border: 1px solid rgba(239, 68, 68, 0.25);">
                  <i class="mdi mdi-fire text-danger" style="font-size: 22px;" aria-hidden="true"></i>
                </div>
              </div>
              <div class="mt-2 text-muted small">
                {{ $t('workspace.streakSubtext') }}
              </div>
            </div>
          </div>
        </div>

        <!-- Gold Reward Pool -->
        <div class="col-sm-6 col-xl-3 grid-margin stretch-card">
          <div class="card shadow-sm h-100">
            <div class="card-body p-3">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <p class="text-muted mb-1 small font-weight-bold text-uppercase">{{ $t('workspace.goldPool') }}</p>
                  <h3 class="mb-0 text-warning font-weight-bold">
                    <i class="mdi mdi-circle-multiple-outline mr-1" aria-hidden="true"></i>{{ formatNumber(gamification.totalGoldCirculating) }}
                  </h3>
                </div>
                <div class="p-2 rounded-lg" style="background: rgba(16, 185, 129, 0.12); border: 1px solid rgba(16, 185, 129, 0.25);">
                  <i class="mdi mdi-database text-success" style="font-size: 22px;" aria-hidden="true"></i>
                </div>
              </div>
              <div class="mt-2 text-muted small">
                {{ $t('workspace.goldPoolSubtext') }}
              </div>
            </div>
          </div>
        </div>

        <!-- Peer Kudos Sent -->
        <div class="col-sm-6 col-xl-3 grid-margin stretch-card">
          <div class="card shadow-sm h-100">
            <div class="card-body p-3">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <p class="text-muted mb-1 small font-weight-bold text-uppercase">{{ $t('workspace.kudosSent') }}</p>
                  <h3 class="mb-0 text-info font-weight-bold">{{ formatNumber(gamification.kudosCount) }} <span class="text-muted small">{{ $t('workspace.pts') }}</span></h3>
                </div>
                <div class="p-2 rounded-lg" style="background: rgba(14, 165, 233, 0.12); border: 1px solid rgba(14, 165, 233, 0.25);">
                  <i class="mdi mdi-hand-heart text-info" style="font-size: 22px;" aria-hidden="true"></i>
                </div>
              </div>
              <div class="mt-2 text-muted small">
                {{ $t('workspace.kudosSubtext') }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Resource Counts Bar (Makers, Players, Rules) -->
      <div class="row mb-4">
        <div v-if="isOwner" class="col-md-4 col-sm-6 grid-margin stretch-card">
          <router-link to="/maker" class="card text-decoration-none shadow-sm h-100">
            <div class="card-body p-3">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <p class="text-muted mb-1 small font-weight-bold text-uppercase">{{ $t('nav.gameMaker') }}</p>
                  <h3 class="mb-0 text-white font-weight-bold">{{ makerCount.data ?? 0 }}</h3>
                </div>
                <div class="p-2 rounded-lg" style="background: rgba(239, 68, 68, 0.12); border: 1px solid rgba(239, 68, 68, 0.25);">
                  <i class="mdi mdi-account-tie text-danger" style="font-size: 22px;" aria-hidden="true"></i>
                </div>
              </div>
              <div class="mt-2 text-muted small">
                {{ $t('workspace.makersCardSub') }}
              </div>
            </div>
          </router-link>
        </div>

        <div :class="isOwner ? 'col-md-4 col-sm-6 grid-margin stretch-card' : 'col-md-6 col-sm-6 grid-margin stretch-card'">
          <router-link to="/player" class="card text-decoration-none shadow-sm h-100">
            <div class="card-body p-3">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <p class="text-muted mb-1 small font-weight-bold text-uppercase">{{ $t('nav.gamePlayer') }}</p>
                  <h3 class="mb-0 text-white font-weight-bold">{{ playerCount.data ?? 0 }}</h3>
                </div>
                <div class="p-2 rounded-lg" style="background: rgba(14, 165, 233, 0.12); border: 1px solid rgba(14, 165, 233, 0.25);">
                  <i class="mdi mdi-account-group text-info" style="font-size: 22px;" aria-hidden="true"></i>
                </div>
              </div>
              <div class="mt-2 text-muted small">
                {{ $t('workspace.playersCardSub') }}
              </div>
            </div>
          </router-link>
        </div>

        <div :class="isOwner ? 'col-md-4 col-sm-12 grid-margin stretch-card' : 'col-md-6 col-sm-6 grid-margin stretch-card'">
          <router-link to="/rules/rewards" class="card text-decoration-none shadow-sm h-100">
            <div class="card-body p-3">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <p class="text-muted mb-1 small font-weight-bold text-uppercase">{{ $t('workspace.activeQuestsCardTitle') }}</p>
                  <h3 class="mb-0 text-white font-weight-bold">{{ ruleCount.data ?? 0 }}</h3>
                </div>
                <div class="p-2 rounded-lg" style="background: rgba(16, 185, 129, 0.12); border: 1px solid rgba(16, 185, 129, 0.25);">
                  <i class="mdi mdi-book-open-page-variant text-success" style="font-size: 22px;" aria-hidden="true"></i>
                </div>
              </div>
              <div class="mt-2 text-muted small">
                {{ $t('workspace.activeQuestsCardSub') }}
              </div>
            </div>
          </router-link>
        </div>
      </div>

      <!-- Division Clash & Hall of Fame Grid -->
      <div class="row mb-4">
        <!-- Squad / Division Clash -->
        <div class="col-lg-7 grid-margin stretch-card">
          <div class="card shadow-sm h-100">
            <div class="card-body p-4">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h5 class="card-title mb-1 text-white font-weight-bold">
                    <i class="mdi mdi-shield-sword-outline text-warning mr-1" aria-hidden="true"></i>
                    {{ $t('workspace.squadClash') }}
                  </h5>
                  <small class="text-muted">{{ $t('workspace.squadClashDesc') }}</small>
                </div>
                <span class="badge badge-outline-warning">
                  <i class="mdi mdi-fire mr-1" aria-hidden="true"></i>{{ $t('workspace.seasonActive') }}
                </span>
              </div>

              <div class="table-responsive">
                <table class="table table-sm table-hover align-middle mb-0" style="width: 100%;">
                  <thead>
                    <tr class="text-muted small text-uppercase">
                      <th style="width: 58px;">{{ $t('workspace.rank') }}</th>
                      <th>{{ $t('workspace.squadDivision') }}</th>
                      <th style="width: 96px;">{{ $t('workspace.members') }}</th>
                      <th style="width: 115px;">{{ $t('workspace.totalGoldPts') }}</th>
                      <th style="width: 110px;">{{ $t('workspace.questProgress') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="!gamification.divisions || gamification.divisions.length === 0">
                      <td colspan="5" class="text-center text-muted py-4 small">
                        {{ $t('workspace.noQuestActivity') }}
                      </td>
                    </tr>
                    <tr v-for="(squad, idx) in gamification.divisions" :key="squad.id || idx">
                      <td class="text-nowrap">
                        <span v-if="idx === 0" class="badge badge-warning text-dark font-weight-bold"><i class="mdi mdi-crown mr-1" aria-hidden="true"></i>#1</span>
                        <span v-else-if="idx === 1" class="badge badge-secondary font-weight-bold"><i class="mdi mdi-medal mr-1" aria-hidden="true"></i>#2</span>
                        <span v-else-if="idx === 2" class="badge badge-dark text-warning font-weight-bold"><i class="mdi mdi-medal-outline mr-1" aria-hidden="true"></i>#3</span>
                        <span v-else class="text-muted">#{{ idx + 1 }}</span>
                      </td>
                      <td>
                        <div class="font-weight-bold text-white" style="line-height: 1.4;">
                          <i class="mdi mdi-shield-account mr-1 text-primary" aria-hidden="true"></i>
                          {{ squad.name }}
                        </div>
                      </td>
                      <td class="text-nowrap">
                        <span class="badge badge-outline-info">{{ squad.members_count ?? squad.players_count ?? 0 }} {{ $t('workspace.staffSuffix') }}</span>
                      </td>
                      <td class="text-warning font-weight-bold text-nowrap">
                        <i class="mdi mdi-circle-multiple-outline mr-1" aria-hidden="true"></i>{{ formatNumber(squad.total_gold || squad.points || 0) }}
                      </td>
                      <td>
                        <div class="progress" style="height: 6px; min-width: 70px;">
                          <div
                            class="progress-bar bg-warning"
                            role="progressbar"
                            :style="{ width: `${squad.progress_rate ?? 0}%` }"
                          ></div>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <!-- Hall of Fame / MVP Players -->
        <div class="col-lg-5 grid-margin stretch-card">
          <div class="card shadow-sm h-100">
            <div class="card-body p-4">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h5 class="card-title mb-1 text-white font-weight-bold">
                    <i class="mdi mdi-crown text-warning mr-1" aria-hidden="true"></i>
                    {{ $t('workspace.hallOfFame') }}
                  </h5>
                  <small class="text-muted">{{ $t('workspace.hallOfFameDesc') }}</small>
                </div>
                <router-link to="/player" class="small text-warning font-weight-bold">
                  {{ $t('workspace.viewAll') }} →
                </router-link>
              </div>

              <div v-if="!gamification.champions || gamification.champions.length === 0" class="text-muted small py-3 text-center">
                {{ $t('workspace.noQuestActivity') }}
              </div>
              <div v-else class="list-group list-group-flush">
                <div
                  v-for="(champ, cIdx) in gamification.champions"
                  :key="champ.id || cIdx"
                  class="d-flex align-items-center justify-content-between p-2 mb-2 rounded border border-subtle bg-subtle"
                >
                  <div class="d-flex align-items-center">
                    <span
                      class="mr-2 font-weight-bold"
                      :class="cIdx === 0 ? 'text-warning' : cIdx === 1 ? 'text-light' : 'text-muted'"
                      style="width: 28px; text-align: center;"
                    >
                      #{{ cIdx + 1 }}
                    </span>
                    <img
                      :src="champ.avatar || 'https://api.dicebear.com/7.x/bottts/svg?seed=' + (champ.name || cIdx)"
                      :alt="champ.name || 'Avatar'"
                      class="rounded-circle mr-2"
                      style="width: 36px; height: 36px; border: 2px solid #f59e0b;"
                    />
                    <div>
                      <div class="font-weight-bold text-white small">{{ champ.name }}</div>
                      <small class="text-muted">{{ $t('workspace.level') }} {{ champ.level ?? 1 }} • {{ champ.division_name || '-' }}</small>
                    </div>
                  </div>
                  <div class="text-right">
                    <span class="badge badge-warning text-dark font-weight-bold">
                      <i class="mdi mdi-circle-multiple-outline mr-1" aria-hidden="true"></i>{{ formatNumber(champ.total_gold || champ.gold || 0) }}
                    </span>
                    <div class="small text-muted mt-1">
                      <i class="mdi mdi-fire text-danger mr-1" aria-hidden="true"></i>{{ champ.streak_days ?? 0 }} {{ $t('workspace.days') }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Live Kudos & Quest Submission Activity Feed -->
      <div class="card mb-4 shadow-sm">
        <div class="card-body p-4">
          <h5 class="card-title mb-3 text-white font-weight-bold">
            <i class="mdi mdi-radar text-info mr-1" aria-hidden="true"></i>
            {{ $t('workspace.liveActivity') }}
          </h5>
          <div class="row">
            <div class="col-md-6 mb-3 mb-md-0 border-right border-subtle">
              <h6 class="text-muted mb-2 font-weight-bold small text-uppercase">
                <i class="mdi mdi-checkbox-marked-circle-outline text-success mr-1" aria-hidden="true"></i>
                {{ $t('workspace.recentQuests') }}
              </h6>
              <div v-if="gamification.activities.length === 0" class="text-muted small py-3">
                {{ $t('workspace.noRecentQuests') }}
              </div>
              <div
                v-for="(act, aIdx) in gamification.activities"
                :key="aIdx"
                class="d-flex align-items-center justify-content-between py-2 border-bottom border-dark"
              >
                <div>
                  <div class="text-white small font-weight-bold">{{ act.user_name || '-' }}</div>
                  <small class="text-muted">{{ act.quest_title || '-' }}</small>
                </div>
                <div class="text-right">
                  <span class="badge badge-outline-success">+{{ act.reward ?? 0 }} XP</span>
                  <div class="text-muted" style="font-size: 10px;">{{ act.time_ago || '-' }}</div>
                </div>
              </div>
            </div>

            <div class="col-md-6">
              <h6 class="text-muted mb-2 font-weight-bold small text-uppercase">
                <i class="mdi mdi-heart-pulse text-danger mr-1" aria-hidden="true"></i>
                {{ $t('workspace.recentKudos') }}
              </h6>
              <div v-if="gamification.kudosFeed.length === 0" class="text-muted small py-3">
                {{ $t('workspace.noRecentKudos') }}
              </div>
              <div
                v-for="(kudos, kIdx) in gamification.kudosFeed"
                :key="kIdx"
                class="d-flex align-items-center justify-content-between py-2 border-bottom border-dark"
              >
                <div>
                  <div class="text-white small">
                    <span class="font-weight-bold text-warning">{{ kudos.from_user }}</span>
                    <span class="text-muted"> → </span>
                    <span class="font-weight-bold text-info">{{ kudos.to_user }}</span>
                  </div>
                  <small class="text-light fst-italic">"{{ kudos.message }}"</small>
                </div>
                <div class="text-right">
                  <span class="badge badge-warning text-dark">{{ kudos.badge || '-' }}</span>
                  <div class="text-muted" style="font-size: 10px;">{{ kudos.time_ago || '-' }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Monthly Quest Completion & Team Growth Chart (Highcharts — Real 12-Month DB Data) -->
      <div class="card mb-4 shadow-sm">
        <div class="card-body p-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h5 class="card-title text-white font-weight-bold mb-1">
                <i class="mdi mdi-chart-areaspline text-primary mr-1" aria-hidden="true"></i>
                {{ $t('workspace.monthlyActivityStatistic') }}
              </h5>
              <small class="text-muted">
                {{ $t('workspace.latest12Months') }}
              </small>
            </div>
            <div>
              <span class="badge badge-outline-info px-3 py-2 font-weight-bold">
                {{ $t('workspace.latest12Months') }}
              </span>
            </div>
          </div>
          <div id="enterprise-subscription-chart" style="height: 300px; width: 100%;"></div>
        </div>
      </div>

      <!-- Company Subscription Invoices Table (Owner & Superadmin Only) -->
      <div v-if="isOwner || isSuperAdmin" class="card mb-4 shadow-sm">
        <div class="card-body p-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h5 class="card-title text-white font-weight-bold mb-0">
              <i class="mdi mdi-receipt-text-outline text-success mr-1" aria-hidden="true"></i>
              {{ $t('workspace.companyInvoices') }}
            </h5>
          </div>
          <div class="table-responsive">
            <table class="table table-hover">
              <thead>
                <tr class="text-muted small text-uppercase">
                  <th>{{ $t('workspace.payDate') }}</th>
                  <th>{{ $t('workspace.billingPeriod') }}</th>
                  <th>{{ $t('workspace.totalAmount') }}</th>
                  <th class="text-right">{{ $t('workspace.downloadInvoice') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(data, index) in biling" :key="index">
                  <td class="font-weight-bold text-white">
                    {{ formatDate(data.billing_transaction?.created_at) }}
                  </td>
                  <td class="text-muted">
                    {{ formatDate(data.billing_period_start) }} - {{ formatDate(data.billing_period_end) }}
                  </td>
                  <td class="text-warning font-weight-bold">
                    Rp {{ formatNumber(data.billing_transaction?.amount || 0) }}
                  </td>
                  <td class="text-right">
                    <a
                      v-if="data.billing_transaction?.invoice"
                      :href="data.billing_transaction.invoice"
                      class="btn btn-sm btn-outline-success font-weight-bold"
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i class="mdi mdi-download mr-1" aria-hidden="true"></i> PDF
                    </a>
                    <span v-else class="text-muted small">-</span>
                  </td>
                </tr>
                <tr v-if="biling.length === 0">
                  <td colspan="4" class="text-center py-4 text-muted">
                    <i class="mdi mdi-file-document-outline" style="font-size: 32px;" aria-hidden="true"></i>
                    <div class="mt-2">{{ $t('common.noData') }}</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Highcharts from "highcharts";
import SuperAdminDashboardView from "./Superadmin/SuperAdminDashboardView.vue";
import {
  countMaker,
  countPlayer,
  countRule,
} from "../services/dashboardService";
import axios from "axios";
import config from "../config/config";
import store from "../store";
import moment from "moment";

export default {
  name: "DashboardView",
  components: {
    SuperAdminDashboardView,
  },
  data() {
    return {
      loading: false,
      timeframe: "month",
      companyName: "",
      makerCount: 0,
      biling: [],
      playerCount: 0,
      ruleCount: 0,
      chartInstance: null,
      monthlyTrends: {
        categories: [],
        approved_quests: [],
        new_members: [],
      },
      gamification: {
        engagementScore: 0,
        engagementLabel: "-",
        streakCount: 0,
        totalGoldCirculating: 0,
        kudosCount: 0,
        divisions: [],
        champions: [],
        activities: [],
        kudosFeed: [],
      },
    };
  },
  watch: {
    "$currentLocale"() {
      if (!this.isSuperAdmin) {
        this.getGamificationData();
        this.$nextTick(() => {
          this.initChart();
        });
      }
    },
  },
  computed: {
    userRolesList() {
      try {
        const u = this.$store.state.user || localStorage.getItem("user");
        if (!u) return [];
        let parsed = typeof u === "string" ? JSON.parse(u) : u;
        if (typeof parsed === "string") parsed = JSON.parse(parsed);
        const userRole = parsed?.data?.role || parsed?.role || "";
        return userRole.split(",").map(r => r.trim().toLowerCase()).filter(Boolean);
      } catch (e) {
        return [];
      }
    },
    isSuperAdmin() {
      return this.userRolesList.includes("superadmin");
    },
    isMaker() {
      return this.userRolesList.includes("maker") && !this.userRolesList.includes("owner") && !this.isSuperAdmin;
    },
    isOwner() {
      return this.userRolesList.includes("owner");
    },
  },
  mounted() {
    this.extractCompanyInfo();
    if (this.isSuperAdmin) {
      return;
    }
    this.refreshAll();
    this.$nextTick(() => {
      this.initChart();
    });
  },
  beforeDestroy() {
    if (this.chartInstance) {
      this.chartInstance.destroy();
      this.chartInstance = null;
    }
  },
  methods: {
    extractCompanyInfo() {
      try {
        const u = localStorage.getItem("user");
        if (u) {
          const parsed = JSON.parse(u);
          const cName = parsed?.data?.company?.name || parsed?.company?.name;
          if (cName) this.companyName = cName;
        }
      } catch (e) {
        // ignore parse error
      }
    },
    refreshAll() {
      this.loading = true;
      if (this.isOwner || this.isSuperAdmin) {
        this.getBiling();
        countMaker().then((data) => {
          this.makerCount = data;
        }).catch(() => {});
      }
      countPlayer().then((data) => {
        this.playerCount = data;
      }).catch(() => {});
      countRule().then((data) => {
        this.ruleCount = data;
      }).catch(() => {});
      this.getGamificationData();
      setTimeout(() => {
        this.loading = false;
      }, 600);
    },
    formatNumber(value) {
      const num = Number(value) || 0;
      return num.toLocaleString(this.$currentLocale === "id" ? "id-ID" : "en-US");
    },
    formatPrice(value) {
      return this.formatNumber(value);
    },
    formatDate(date) {
      if (!date) return "-";
      return moment(date).format("DD MMM YYYY");
    },
    formatEngagementLabel(label) {
      if (!label) return "-";
      const lower = String(label).toLowerCase();
      if (lower.includes("sangat aktif") || lower.includes("highly active")) {
        return this.$t("workspace.ratingVeryActive");
      }
      if (lower.includes("aktif") || lower.includes("active")) {
        return this.$t("workspace.ratingActive");
      }
      if (lower.includes("baru mulai") || lower.includes("getting started")) {
        return this.$t("workspace.ratingGettingStarted");
      }
      return label;
    },
    buildFallbackMonthCategories() {
      const isId = this.$currentLocale === "id";
      const monthsId = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
      const monthsEn = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const list = [];
      const now = new Date();
      for (let i = 11; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const mLabel = isId ? monthsId[d.getMonth()] : monthsEn[d.getMonth()];
        list.push(`${mLabel} ${String(d.getFullYear()).slice(-2)}`);
      }
      return list;
    },
    initChart() {
      const container = document.getElementById("enterprise-subscription-chart");
      if (!container) return;

      if (this.chartInstance) {
        this.chartInstance.destroy();
      }

      const categories = Array.isArray(this.monthlyTrends.categories) && this.monthlyTrends.categories.length > 0
        ? this.monthlyTrends.categories
        : this.buildFallbackMonthCategories();
      const approvedData = Array.isArray(this.monthlyTrends.approved_quests) && this.monthlyTrends.approved_quests.length > 0
        ? this.monthlyTrends.approved_quests
        : new Array(categories.length).fill(0);
      const membersData = Array.isArray(this.monthlyTrends.new_members) && this.monthlyTrends.new_members.length > 0
        ? this.monthlyTrends.new_members
        : new Array(categories.length).fill(0);

      this.chartInstance = Highcharts.chart("enterprise-subscription-chart", {
        chart: {
          backgroundColor: "#061531",
          borderRadius: 8,
        },
        accessibility: { enabled: false },
        title: { text: null },
        xAxis: {
          categories,
          labels: { style: { color: "#93c5fd" } },
          lineColor: "#1e4b85",
        },
        yAxis: {
          allowDecimals: false,
          min: 0,
          title: { text: this.$t("workspace.chartActivityAxis"), style: { color: "#93c5fd" } },
          gridLineColor: "rgba(56, 189, 248, 0.14)",
          labels: { style: { color: "#93c5fd" } },
        },
        tooltip: {
          backgroundColor: "#0b2247",
          borderColor: "#38bdf8",
          style: { color: "#ffffff" },
          shared: true,
        },
        legend: {
          itemStyle: { color: "#e0f2fe" },
          itemHoverStyle: { color: "#fde047" },
        },
        credits: { enabled: false },
        series: [
          {
            name: this.$t("workspace.approvedQuestsSeries"),
            color: "#38bdf8",
            data: approvedData,
          },
          {
            name: this.$t("workspace.newMembersSeries"),
            color: "#fbbf24",
            data: membersData,
          },
        ],
      });
    },
    getGamificationData() {
      const API_URL = config.API_URL;
      const token = store.state.authToken || localStorage.getItem("authToken") || localStorage.getItem("token");
      if (!token) return;

      const headers = { Authorization: `Bearer ${token}` };
      const params = { timeframe: this.timeframe, period: this.timeframe };

      // 1. Fetch Engagement Score
      axios
        .get(`${API_URL}owner/dashboard/engagement-score`, { headers, params })
        .then((res) => {
          if (res.data && res.data.data) {
            const d = res.data.data;
            if (d.engagement_score !== undefined) {
              this.gamification.engagementScore = d.engagement_score;
            }
            if (d.label) {
              this.gamification.engagementLabel = d.label;
            }
          }
        })
        .catch(() => {});

      // 2. Fetch Team Performance
      axios
        .get(`${API_URL}owner/dashboard/team-performance`, { headers, params })
        .then((res) => {
          if (res.data && res.data.data) {
            const d = res.data.data;
            if (Array.isArray(d.top_performers)) {
              this.gamification.champions = d.top_performers;
            }
          }
        })
        .catch(() => {});

      // 3. Fetch Divisions
      axios
        .get(`${API_URL}player/leaderboard/divisions`, { headers, params })
        .then((res) => {
          if (res.data && Array.isArray(res.data.data)) {
            this.gamification.divisions = res.data.data;
          }
        })
        .catch(() => {});

      // 4. Fetch Activity Feed & Real 12-Month Trends
      axios
        .get(`${API_URL}owner/dashboard/activity-feed`, { headers, params })
        .then((res) => {
          if (res.data && res.data.data) {
            const d = res.data.data;
            if (Array.isArray(d.recent_submissions)) {
              this.gamification.activities = d.recent_submissions;
            }
            if (Array.isArray(d.recent_kudos)) {
              this.gamification.kudosFeed = d.recent_kudos;
            }
            if (d.active_streaks_count !== undefined) {
              this.gamification.streakCount = d.active_streaks_count;
            }
            if (d.total_gold_circulating !== undefined) {
              this.gamification.totalGoldCirculating = d.total_gold_circulating;
            }
            if (d.total_kudos_count !== undefined) {
              this.gamification.kudosCount = d.total_kudos_count;
            }
            if (d.monthly_trends) {
              this.monthlyTrends = d.monthly_trends;
              this.$nextTick(() => {
                this.initChart();
              });
            }
          }
        })
        .catch(() => {});
    },
    getBiling() {
      if (!this.isOwner && !this.isSuperAdmin) return;
      const API_URL = config.API_URL;
      const token = store.state.authToken || localStorage.getItem("authToken") || localStorage.getItem("token");
      if (!token) return;

      axios
        .get(`${API_URL}billing`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then((resp) => {
          this.biling = resp.data.data || [];
        })
        .catch(() => {});
    },
  },
};
</script>

<style scoped>
.enterprise-workspace {
  animation: fadeIn 0.25s ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
